/* global window, navigator, document */
const ARTICLE_SELECTOR = '.article-content[data-article-id]';
const DEFAULT_BATCH_SIZE = 20;
const MAX_PUBLIC_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL = 4000;
const INITIAL_FLUSH_INTERVAL = 250;
const NAVIGATION_FLUSH_INTERVAL = 100;
const READ_PROGRESS_TICK_MS = 1000;
const READ_PROGRESS_REPORT_INTERVAL_SECONDS = 5;
const MIN_VISIBLE_ARTICLE_PIXELS = 96;
const JOURNEY_TRANSFER_PARAM = 'hc_jid';
const SESSION_TRANSFER_PARAM = 'hc_sid';
const JOURNEY_STORAGE_KEY = 'hc_journey_id';
const SESSION_STORAGE_KEY = 'hc_session_id';
let transferredIdsCache = null;

const getTrackedArticle = () => document.querySelector(ARTICLE_SELECTOR);

const deviceType = () => {
    if (typeof navigator === 'undefined') {
        return 'desktop';
    }
    const ua = navigator.userAgent || '';
    if (/bot|crawler|spider|crawling/i.test(ua)) {
        return 'bot';
    }
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
        return 'tablet';
    }
    if (/mobi|iphone|android/i.test(ua)) {
        return 'mobile';
    }
    return 'desktop';
};

const uuidv4 = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const buildPageUrl = () => {
    if (typeof window === 'undefined') {
        return '';
    }
    return window.location.href;
};

const normalizeLinkUrl = (value) => {
    if (!value || typeof window === 'undefined') {
        return '';
    }

    try {
        return new URL(value, window.location.href).toString();
    } catch (error) {
        return '';
    }
};

const safeNowIso = () => new Date().toISOString();

const readTransferredIds = () => {
    if (transferredIdsCache) {
        return transferredIdsCache;
    }

    if (typeof window === 'undefined') {
        transferredIdsCache = {
            journeyId: '',
            sessionId: '',
        };
        return transferredIdsCache;
    }

    try {
        const currentUrl = new URL(window.location.href);
        const transferredJourneyId = currentUrl.searchParams.get(JOURNEY_TRANSFER_PARAM) || '';
        const transferredSessionId = currentUrl.searchParams.get(SESSION_TRANSFER_PARAM) || '';
        if (transferredJourneyId) {
            currentUrl.searchParams.delete(JOURNEY_TRANSFER_PARAM);
        }
        if (transferredSessionId) {
            currentUrl.searchParams.delete(SESSION_TRANSFER_PARAM);
        }
        if (transferredJourneyId || transferredSessionId) {
            if (typeof window.history?.replaceState === 'function') {
                window.history.replaceState({}, document.title, currentUrl.toString());
            }
        }

        transferredIdsCache = {
            journeyId: transferredJourneyId,
            sessionId: transferredSessionId,
        };
        return transferredIdsCache;
    } catch (error) {
        transferredIdsCache = {
            journeyId: '',
            sessionId: '',
        };
        return transferredIdsCache;
    }
};

const readSessionId = () => {
    const { sessionId: transferredSessionId } = readTransferredIds();
    if (typeof sessionStorage === 'undefined') {
        return transferredSessionId || uuidv4();
    }
    if (transferredSessionId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, transferredSessionId);
        return transferredSessionId;
    }
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
        return existing;
    }
    const next = uuidv4();
    sessionStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
};

const readJourneyId = () => {
    const { journeyId: transferredJourneyId } = readTransferredIds();
    if (typeof sessionStorage === 'undefined') {
        return transferredJourneyId || uuidv4();
    }
    if (transferredJourneyId) {
        sessionStorage.setItem(JOURNEY_STORAGE_KEY, transferredJourneyId);
        return transferredJourneyId;
    }
    const existing = sessionStorage.getItem(JOURNEY_STORAGE_KEY);
    if (existing) {
        return existing;
    }
    const next = uuidv4();
    sessionStorage.setItem(JOURNEY_STORAGE_KEY, next);
    return next;
};

class HCBeaconClient {
    constructor() {
        this.config = null;
        this.queue = [];
        this.flushTimer = null;
        this.flushDueAt = null;
        this.sessionId = '';
        this.journeyId = '';
        this.readProgressTimer = null;
        this.readTrackedMs = 0;
        this.readActiveSince = null;
        this.lastReportedReadSeconds = 0;
        this.pageViewTracked = false;
        this.articleObserved = false;
        this.searchContext = {
            query: '',
            searchToken: '',
            topHit: '',
        };
        this.lastVisibility = document.visibilityState;
        this.visible = !document.hidden;
        this.focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handlePageExit = this.handlePageExit.bind(this);
        this.handleArticleActivity = this.handleArticleActivity.bind(this);
    }

    init(config) {
        if (!config || !config.endpoint || !config.authToken) {
            return;
        }
        this.config = {
            endpoint: config.endpoint,
            authToken: config.authToken,
            batchSize: Math.min(config.batchSize || DEFAULT_BATCH_SIZE, MAX_PUBLIC_BATCH_SIZE),
            flushInterval: config.flushInterval || DEFAULT_FLUSH_INTERVAL,
            enabled: config.enabled !== false,
            isAllowed: typeof config.isAllowed === 'function' ? config.isAllowed : () => true,
            defaultContext: config.defaultContext || {},
            autoTrackPageView: config.autoTrackPageView !== false,
            autoReadProgress: config.autoReadProgress !== false,
            autoSearchInstrumentation: config.autoSearchInstrumentation !== false,
            autoExternalLinkTracking: config.autoExternalLinkTracking !== false,
        };

        if (!this.config.enabled || !this.config.isAllowed()) {
            return;
        }

        this.sessionId = this.sessionId || readSessionId();
        this.journeyId = this.journeyId || readJourneyId();

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.handleFocus);
        window.addEventListener('blur', this.handleFocus);
        if (this.config.autoReadProgress) {
            window.addEventListener('scroll', this.handleArticleActivity, { passive: true });
            window.addEventListener('resize', this.handleArticleActivity);
        }
        window.addEventListener('pagehide', this.handlePageExit);
        window.addEventListener('beforeunload', this.handlePageExit);

        if (this.config.autoTrackPageView) {
            this.maybeTrackPageView();
        }
        if (this.config.autoReadProgress) {
            this.startReadProgress();
        }
        if (this.config.autoSearchInstrumentation) {
            this.attachSearchInstrumentation();
        }
        if (this.config.autoExternalLinkTracking) {
            this.attachExternalLinkInstrumentation();
        }
    }

    buildBaseEvent(eventType, payload = {}) {
        const normalizedPayload = this.normalizeContextPayload(payload);
        const pageUrl = normalizedPayload.page_url || this.resolvePageUrl(payload);

        return {
            event_id: uuidv4(),
            event_type: eventType,
            ts: safeNowIso(),
            session_id: this.sessionId,
            journey_id: this.journeyId,
            page_url: pageUrl,
            device: deviceType(),
            referrer: normalizedPayload.referrer || '',
            surface: normalizedPayload.surface || '',
            widget_id: normalizedPayload.widget_id || '',
            nudge_id: normalizedPayload.nudge_id || '',
            faq_section_id: normalizedPayload.faq_section_id || '',
            embed_host: normalizedPayload.embed_host || '',
            transition_target: normalizedPayload.transition_target || '',
            article_view_type: normalizedPayload.article_view_type || '',
        };
    }

    setSearchContext({ query, searchToken, topHit }) {
        if (query) {
            this.searchContext.query = query;
        }
        if (searchToken) {
            this.searchContext.searchToken = searchToken;
        }
        if (topHit) {
            this.searchContext.topHit = topHit;
        }
    }

    stop() {
        if (this.config) this.config.enabled = false;
        this.queue = [];
        clearTimeout(this.flushTimer);
        clearInterval(this.readProgressTimer);
        this.flushTimer = null;
        this.flushDueAt = null;
    }

    enqueue(event, { flushDelay = null, flushNow = false } = {}) {
        if (!this.config || !this.config.enabled || !this.config.isAllowed()) {
            this.stop();
            return;
        }
        this.queue.push(event);
        if (this.queue.length >= this.config.batchSize) {
            this.flush();
            return;
        }
        if (flushNow) {
            this.flush(true);
            return;
        }
        const delay = typeof flushDelay === 'number' ? flushDelay : this.config.flushInterval;
        this.ensureFlushTimer(delay);
    }

    ensureFlushTimer(delay = this.config.flushInterval) {
        const now = Date.now();
        const dueAt = now + delay;
        if (this.flushTimer && this.flushDueAt !== null && this.flushDueAt <= dueAt) {
            return;
        }
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
        }
        this.flushDueAt = dueAt;
        this.flushTimer = setTimeout(() => {
            this.flushDueAt = null;
            this.flush();
        }, delay);
    }

    flush(immediate = false) {
        if (!this.config || !this.config.enabled || !this.config.isAllowed()) {
            this.stop();
            return;
        }
        if (!this.queue.length) {
            if (this.flushTimer) {
                clearTimeout(this.flushTimer);
                this.flushTimer = null;
            }
            this.flushDueAt = null;
            return;
        }
        const payload = JSON.stringify({
            auth_token: this.config.authToken,
            events: this.queue,
        });
        this.queue = [];
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        this.flushDueAt = null;

        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            if (navigator.sendBeacon(this.config.endpoint, payload)) {
                return;
            }
        }

        if (typeof fetch !== 'undefined') {
            fetch(this.config.endpoint, {
                method: 'POST',
                body: payload,
                keepalive: true,
                headers: {
                    'Content-Type': 'text/plain;charset=UTF-8',
                },
            }).catch(() => {});
        }
    }

    normalizeContextPayload(payload = {}) {
        const defaultContext = this.config?.defaultContext || {};
        const rawPageUrl = payload.pageUrl || payload.page_url || defaultContext.page_url || '';

        return {
            page_url: rawPageUrl,
            referrer: payload.referrer || defaultContext.referrer || document.referrer || '',
            surface: payload.surface || defaultContext.surface || '',
            widget_id: payload.widgetId || payload.widget_id || defaultContext.widget_id || '',
            nudge_id: payload.nudgeId || payload.nudge_id || defaultContext.nudge_id || '',
            faq_section_id: payload.faqSectionId || payload.faq_section_id || defaultContext.faq_section_id || '',
            embed_host: payload.embedHost || payload.embed_host || defaultContext.embed_host || normalizeHostFromPageUrl(rawPageUrl || buildPageUrl()),
            transition_target: payload.transitionTarget || payload.transition_target || defaultContext.transition_target || '',
            article_view_type: payload.articleViewType || payload.article_view_type || defaultContext.article_view_type || '',
        };
    }

    resolvePageUrl(payload = {}) {
        const defaultContext = this.config?.defaultContext || {};
        if (payload.pageUrl || payload.page_url) {
            return payload.pageUrl || payload.page_url;
        }
        if (typeof defaultContext.pageUrlResolver === 'function') {
            return defaultContext.pageUrlResolver();
        }
        if (typeof this.config?.pageUrlResolver === 'function') {
            return this.config.pageUrlResolver();
        }
        return defaultContext.page_url || buildPageUrl();
    }

    trackPageView(payload = {}) {
        const shouldTreatAsAutoPageView = Object.keys(payload || {}).length === 0;
        if (shouldTreatAsAutoPageView && this.pageViewTracked) {
            return;
        }
        const event = {
            ...this.buildBaseEvent('page_view', payload),
        };
        if (shouldTreatAsAutoPageView) {
            this.pageViewTracked = true;
        }
        this.enqueue(event, { flushDelay: INITIAL_FLUSH_INTERVAL });
    }

    trackArticleView(articleOrPayload, articleViewType = 'full_article_open') {
        let articleId = articleOrPayload;
        let resolvedType = articleViewType;
        let payload = {};
        if (articleOrPayload && typeof articleOrPayload === 'object') {
            articleId = articleOrPayload.articleId || articleOrPayload.article_id || '';
            resolvedType = articleOrPayload.articleViewType
                || articleOrPayload.article_view_type
                || articleOrPayload.type
                || articleViewType;
            payload = articleOrPayload;
        }
        if (!articleId) {
            return;
        }
        const event = {
            ...this.buildBaseEvent('article_view', {
                ...payload,
                article_view_type: resolvedType,
            }),
            article_id: String(articleId),
        };
        this.enqueue(event, { flushDelay: NAVIGATION_FLUSH_INTERVAL });
    }

    trackReadProgress(articleIdOrPayload, secondsRead) {
        let articleId = articleIdOrPayload;
        let payload = {};
        let resolvedSecondsRead = secondsRead;
        if (articleIdOrPayload && typeof articleIdOrPayload === 'object') {
            articleId = articleIdOrPayload.articleId || articleIdOrPayload.article_id || '';
            resolvedSecondsRead = articleIdOrPayload.secondsRead || articleIdOrPayload.seconds_read || secondsRead;
            payload = articleIdOrPayload;
        }
        if (!articleId || resolvedSecondsRead <= 0) {
            return;
        }
        const event = {
            ...this.buildBaseEvent('read_progress', payload),
            article_id: String(articleId),
            seconds_read: resolvedSecondsRead,
        };
        this.enqueue(event);
    }

    trackSearch({
        query,
        resultIds,
        totalResults,
        searchToken,
        topHit,
        isInstant = false,
        aiEnabled = null,
        aiAnswerRequested = null,
        aiAnswerReceived = null,
        aiQueryLogId = '',
        ...payload
    }) {
        if (!query) {
            return;
        }
        const resolvedTopHit = topHit || (Array.isArray(resultIds) && resultIds.length > 0 ? resultIds[0] : '');
        this.setSearchContext({
            query,
            searchToken,
            topHit: resolvedTopHit,
        });
        const event = {
            ...this.buildBaseEvent('search', payload),
            search_query: query,
            search_result_ids: Array.isArray(resultIds) ? resultIds.slice(0, MAX_PUBLIC_BATCH_SIZE) : [],
            search_token: searchToken || '',
            top_hit: resolvedTopHit ? String(resolvedTopHit) : '',
            is_instant: Boolean(isInstant),
            ai_enabled: aiEnabled === null || aiEnabled === undefined ? null : Boolean(aiEnabled),
            ai_answer_requested: aiAnswerRequested === null || aiAnswerRequested === undefined ? null : Boolean(aiAnswerRequested),
            ai_answer_received: aiAnswerReceived === null || aiAnswerReceived === undefined ? null : Boolean(aiAnswerReceived),
            ai_query_log_id: aiQueryLogId ? String(aiQueryLogId) : '',
        };
        if (typeof totalResults === 'number') {
            event.total_results = totalResults;
        }
        this.enqueue(event);
    }

    trackSearchClick({ query, pickedId, pickedRank, searchToken, ...payload }) {
        const resolvedQuery = query || this.searchContext.query;
        const resolvedToken = searchToken || this.searchContext.searchToken;
        if (!resolvedQuery || !pickedId) {
            return;
        }
        const event = {
            ...this.buildBaseEvent('search_click', payload),
            search_query: resolvedQuery,
            search_token: resolvedToken || '',
            top_hit: this.searchContext.topHit || '',
            picked_id: pickedId,
            search_click_rank: pickedRank,
        };
        this.enqueue(event, { flushNow: true });
    }

    trackExternalExitClick({ externalLinkUrl, externalLinkAnchor, ...payload }) {
        const resolvedLinkUrl = normalizeLinkUrl(externalLinkUrl);
        if (!resolvedLinkUrl) {
            return;
        }

        const event = {
            ...this.buildBaseEvent('external_exit_click', payload),
            external_link_url: resolvedLinkUrl,
            external_link_anchor: String(externalLinkAnchor || '').trim().slice(0, 512),
        };
        this.enqueue(event, { flushNow: true });
    }

    trackSupportHandoffClick(payload = {}) {
        const event = {
            ...this.buildBaseEvent('support_handoff_click', payload),
        };

        this.enqueue(event, { flushNow: true });
    }

    trackWidgetLoad(payload = {}) {
        const event = {
            ...this.buildBaseEvent('widget_load', payload),
        };

        this.enqueue(event, { flushDelay: INITIAL_FLUSH_INTERVAL });
    }

    trackWidgetOpen(payload = {}) {
        const event = {
            ...this.buildBaseEvent('widget_open', payload),
        };

        this.enqueue(event, { flushNow: true });
    }

    trackWidgetCollapse(payload = {}) {
        const event = {
            ...this.buildBaseEvent('widget_collapse', payload),
        };

        this.enqueue(event, { flushNow: true });
    }

    /**
     * Proactive-nudge instrumentation. All three are driven host-side from
     * init_widget.js: the engine decides, the iframe renders, and the host
     * reports what actually happened. A nudge id is mandatory — an event we
     * cannot attribute to a nudge is worthless in the aggregation, so it is
     * dropped rather than sent with an empty dimension.
     */
    resolveNudgeId(payload = {}) {
        const nudgeId = payload.nudgeId || payload.nudge_id || '';
        return nudgeId ? String(nudgeId) : '';
    }

    trackNudgeImpression(payload = {}) {
        const nudgeId = this.resolveNudgeId(payload);
        if (!nudgeId) {
            return;
        }

        const event = {
            ...this.buildBaseEvent('nudge_impression', { ...payload, nudge_id: nudgeId }),
            nudge_id: nudgeId,
        };

        // Impressions are passive, so they ride the normal batch window; a
        // click or dismissal that follows flushes them along with itself.
        this.enqueue(event);
    }

    trackNudgeClick(payload = {}) {
        const nudgeId = this.resolveNudgeId(payload);
        if (!nudgeId) {
            return;
        }

        const event = {
            ...this.buildBaseEvent('nudge_click', { ...payload, nudge_id: nudgeId }),
            nudge_id: nudgeId,
        };

        this.enqueue(event, { flushNow: true });
    }

    trackNudgeDismiss(payload = {}) {
        const nudgeId = this.resolveNudgeId(payload);
        if (!nudgeId) {
            return;
        }

        const event = {
            ...this.buildBaseEvent('nudge_dismiss', { ...payload, nudge_id: nudgeId }),
            nudge_id: nudgeId,
        };

        this.enqueue(event, { flushNow: true });
    }

    trackHelpcenterTransition({ transitionTarget, ...payload }) {
        if (!transitionTarget) {
            return;
        }

        const event = {
            ...this.buildBaseEvent('helpcenter_transition', {
                ...payload,
                transition_target: transitionTarget,
            }),
            transition_target: transitionTarget,
        };

        this.enqueue(event, { flushNow: true });
    }

    trackContactRequest(payload = {}) {
        const event = {
            ...this.buildBaseEvent('contact_request', payload),
        };

        this.enqueue(event, { flushNow: true });
    }

    handleVisibilityChange() {
        const isHidden = document.hidden;
        if (isHidden) {
            this.syncReadProgress({ force: true });
        }
        this.visible = !isHidden;
        this.lastVisibility = document.visibilityState;
        if (!isHidden) {
            this.maybeTrackPageView();
            this.syncReadProgress({ force: true });
        }
    }

    handleFocus() {
        const isFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        if (!isFocused) {
            this.syncReadProgress({ force: true });
        }
        this.focused = isFocused;
        if (isFocused) {
            this.maybeTrackPageView();
        }
        this.syncReadProgress({ force: true });
    }

    handlePageExit() {
        this.syncReadProgress({ force: true });
        this.flush(true);
    }

    handleArticleActivity() {
        this.maybeTrackPageView();
        this.syncReadProgress();
    }

    maybeTrackPageView() {
        if (!this.visible || !this.focused || this.config?.autoTrackPageView === false) {
            return;
        }
        this.trackPageView();
    }

    getArticleContext() {
        const article = getTrackedArticle();
        if (!article) {
            return null;
        }
        const articleId = article.getAttribute('data-article-id');
        if (!articleId) {
            return null;
        }
        return { article, articleId: String(articleId) };
    }

    getVisibleArticleHeight(article) {
        const rect = article.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        return Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
    }

    isArticleVisibleForTracking(article) {
        const rect = article.getBoundingClientRect();
        if (rect.height <= 0) {
            return false;
        }
        const visibleHeight = this.getVisibleArticleHeight(article);
        const requiredVisibleHeight = Math.max(
            Math.min(rect.height * 0.2, MIN_VISIBLE_ARTICLE_PIXELS),
            Math.min(MIN_VISIBLE_ARTICLE_PIXELS, rect.height),
        );
        return visibleHeight >= requiredVisibleHeight;
    }

    getCurrentReadSeconds(now) {
        const activeMs = this.readActiveSince ? now - this.readActiveSince : 0;
        return Math.floor((this.readTrackedMs + activeMs) / 1000);
    }

    maybeReportReadProgress(articleId, secondsRead, force = false) {
        if (secondsRead <= this.lastReportedReadSeconds) {
            return;
        }
        if (!force && (secondsRead - this.lastReportedReadSeconds) < READ_PROGRESS_REPORT_INTERVAL_SECONDS) {
            return;
        }
        this.lastReportedReadSeconds = secondsRead;
        this.trackReadProgress(articleId, secondsRead);
    }

    syncReadProgress({ force = false } = {}) {
        const context = this.getArticleContext();
        const now = Date.now();
        const isActivelyReading = Boolean(
            context &&
            this.visible &&
            this.focused &&
            this.isArticleVisibleForTracking(context.article),
        );

        if (isActivelyReading) {
            if (!this.articleObserved) {
                this.articleObserved = true;
                this.trackArticleView(context.articleId, 'full_article_open');
            }
            if (this.readActiveSince === null) {
                this.readActiveSince = now;
            }
            this.maybeReportReadProgress(context.articleId, this.getCurrentReadSeconds(now), force);
            return;
        }

        if (this.readActiveSince !== null) {
            this.readTrackedMs += now - this.readActiveSince;
            this.readActiveSince = null;
        }

        if (context) {
            this.maybeReportReadProgress(context.articleId, this.getCurrentReadSeconds(now), force);
        }
    }

    startReadProgress() {
        const context = this.getArticleContext();
        if (!context) {
            return;
        }
        if (this.readProgressTimer) {
            clearInterval(this.readProgressTimer);
        }

        this.syncReadProgress();

        this.readProgressTimer = setInterval(() => {
            this.syncReadProgress();
        }, READ_PROGRESS_TICK_MS);
    }

    attachSearchInstrumentation() {
        const searchField = document.getElementById('search-field');
        if (!searchField) {
            return;
        }

        document.addEventListener('click', (event) => {
            const target = event.target.closest('.search-result');
            if (!target) {
                return;
            }
            if (target.closest('#search-field')) {
                // Instant-search interactions are tracked explicitly from SearchField.jsx.
                return;
            }
            const query = target.getAttribute('data-query') || searchField.getAttribute('data-query') || this.searchContext.query || '';
            const searchToken = target.getAttribute('data-search-token') || searchField.getAttribute('data-search-token') || this.searchContext.searchToken || '';
            const pickedId = target.getAttribute('data-article-id');
            const pickedRank = parseInt(target.getAttribute('data-rank') || '0', 10);
            if (query && pickedId) {
                this.trackSearchClick({
                    query,
                    searchToken,
                    pickedId,
                    pickedRank,
                });
            }
        });
    }

    attachExternalLinkInstrumentation() {
        document.addEventListener('click', (event) => {
            const target = event.target.closest('a[href]');
            if (!target) {
                return;
            }

            const href = target.getAttribute('href') || '';
            const resolvedUrl = normalizeLinkUrl(href);
            if (!resolvedUrl) {
                return;
            }

            let destination;
            let current;
            try {
                destination = new URL(resolvedUrl);
                current = new URL(window.location.href);
            } catch (error) {
                return;
            }

            if (!['http:', 'https:'].includes(destination.protocol)) {
                return;
            }

            if (destination.origin === current.origin) {
                return;
            }

            this.trackExternalExitClick({
                externalLinkUrl: resolvedUrl,
                externalLinkAnchor: target.getAttribute('data-analytics-label')
                    || target.getAttribute('aria-label')
                    || target.getAttribute('title')
                    || target.textContent
                    || '',
            });
        }, true);
    }
}

const beacon = new HCBeaconClient();

const normalizeHostFromPageUrl = (value) => {
    if (!value) {
        return '';
    }

    try {
        return new URL(value, window.location.href).hostname.toLowerCase();
    } catch (error) {
        return '';
    }
};

window.HCBeacon = {
    init: (config) => beacon.init(config),
    stop: () => beacon.stop(),
    getSessionId: () => beacon.sessionId,
    getJourneyId: () => beacon.journeyId,
    trackPageView: (payload) => beacon.trackPageView(payload),
    trackSearch: (payload) => beacon.trackSearch(payload),
    trackSearchClick: (payload) => beacon.trackSearchClick(payload),
    trackArticleView: (payload) => beacon.trackArticleView(payload),
    trackReadProgress: (payload, secondsRead) => beacon.trackReadProgress(payload, secondsRead),
    trackExternalExitClick: (payload) => beacon.trackExternalExitClick(payload),
    trackContactRequest: (payload) => beacon.trackContactRequest(payload),
    trackSupportHandoffClick: (payload) => beacon.trackSupportHandoffClick(payload),
    trackWidgetLoad: (payload) => beacon.trackWidgetLoad(payload),
    trackWidgetOpen: (payload) => beacon.trackWidgetOpen(payload),
    trackWidgetCollapse: (payload) => beacon.trackWidgetCollapse(payload),
    trackNudgeImpression: (payload) => beacon.trackNudgeImpression(payload),
    trackNudgeClick: (payload) => beacon.trackNudgeClick(payload),
    trackNudgeDismiss: (payload) => beacon.trackNudgeDismiss(payload),
    trackHelpcenterTransition: (payload) => beacon.trackHelpcenterTransition(payload),
};

export default beacon;
